/**
 * Sprint 233 — Admin Creator Package Order Queue.
 * Lists all package orders with status filter + creator assignment workflow.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Search, Receipt, RefreshCw, ChevronRight,
  Sparkles, Users, Plus, X, CheckCircle2, Clock, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  new_order:                   { label: 'New Order',                color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  awaiting_quote:              { label: 'Awaiting Quote',           color: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200' },
  awaiting_payment:            { label: 'Awaiting Payment',         color: 'bg-amber-100 text-amber-700 border-amber-200' },
  awaiting_creator_assignment: { label: 'Awaiting Creator',         color: 'bg-purple-100 text-purple-700 border-purple-200' },
  creator_assigned:            { label: 'Creator Assigned',         color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  in_progress:                 { label: 'In Progress',              color: 'bg-blue-100 text-blue-700 border-blue-200' },
  content_submitted:           { label: 'Content Submitted',        color: 'bg-violet-100 text-violet-700 border-violet-200' },
  delivered:                   { label: 'Delivered',                color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  completed:                   { label: 'Completed',                color: 'bg-emerald-200 text-emerald-800 border-emerald-300' },
  cancelled:                   { label: 'Cancelled',                color: 'bg-gray-200 text-gray-700 border-gray-300' },
};

const PACKAGE_ORDER_STATUSES = [
  'new_order', 'awaiting_quote', 'awaiting_payment',
  'awaiting_creator_assignment', 'creator_assigned',
  'in_progress', 'content_submitted', 'delivered', 'completed', 'cancelled',
];

const AdminPackages = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [openOrderId, setOpenOrderId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/package-orders${statusFilter ? `?status=${statusFilter}` : ''}`);
      setOrders(res.data || []);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = orders.filter(o => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [o.client_name, o.package?.name, o.package_slug, o.id].some(s => (s || '').toLowerCase().includes(q));
  });

  const counts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-packages">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Sparkles className="w-5 h-5 text-[#0DEAE3]" />
          <h1 className="text-lg font-semibold">Creator Package Orders</h1>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <div className="max-w-7xl mx-auto px-4 pb-3 flex flex-wrap items-center gap-2 text-xs">
          <button onClick={() => setStatusFilter('')}
                  className={`px-2.5 py-1 rounded-full border ${!statusFilter ? 'bg-cyan-600 border-cyan-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                  data-testid="filter-all">
            All ({orders.length})
          </button>
          {PACKAGE_ORDER_STATUSES.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
                    className={`px-2.5 py-1 rounded-full border ${statusFilter === s ? 'bg-cyan-600 border-cyan-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                    data-testid={`filter-${s}`}>
              {STATUS_META[s]?.label || s} ({counts[s] || 0})
            </button>
          ))}
          <div className="flex-1" />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by client / package" value={search} onChange={(e) => setSearch(e.target.value)}
                   className="pl-9 h-8 text-xs w-64" data-testid="search-input" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        {loading ? (
          <div className="text-center py-16 text-gray-500"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Receipt className="w-10 h-10 mx-auto text-gray-300" />
            <p className="mt-2 text-sm">No package orders yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(o => (
              <OrderRow key={o.id} order={o} onOpen={() => setOpenOrderId(o.id)} />
            ))}
          </div>
        )}
      </main>

      {openOrderId && (
        <OrderDrawer orderId={openOrderId} onClose={() => setOpenOrderId(null)} onChange={load} />
      )}
    </div>
  );
};

const OrderRow = ({ order, onOpen }) => {
  const m = STATUS_META[order.status] || { label: order.status, color: 'bg-gray-100 text-gray-700 border-gray-200' };
  return (
    <button type="button" onClick={onOpen} data-testid={`order-row-${order.id}`}
            className="w-full text-left bg-white border rounded-lg p-3 hover:border-cyan-300 transition-colors flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm text-gray-900 truncate">{order.package?.name || order.package_slug}</p>
          <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${m.color}`}>
            {m.label}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px]">{order.mode === 'auto_match' ? 'Auto-Match' : 'Browse & Pick'}</span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          <span className="font-medium">{order.client_name}</span>
          {' · '}<span>{new Date(order.created_at).toLocaleString()}</span>
          {order.category && <> · <span>{order.category}</span></>}
          {(order.assignments || []).length > 0 && (
            <> · <span className="text-cyan-700">{(order.assignments || []).length} creator{order.assignments.length !== 1 ? 's' : ''} assigned</span></>
          )}
        </p>
      </div>
      <div className="text-right">
        <p className="font-bold text-emerald-700">
          {order.amount_cents == null
            ? (order.quote_amount_cents ? `$${(order.quote_amount_cents / 100).toFixed(0)} quote` : 'Quote')
            : `$${(order.amount_cents / 100).toFixed(0)}`}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-gray-400">{order.payment_status}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400" />
    </button>
  );
};

const OrderDrawer = ({ orderId, onClose, onChange }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorResults, setCreatorResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [quoteCents, setQuoteCents] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/package-orders/${orderId}`);
      setOrder(res.data);
      setAdminNotes(res.data.admin_notes || '');
      setQuoteCents(res.data.quote_amount_cents ? String(res.data.quote_amount_cents / 100) : '');
    } catch {
      toast.error('Failed to load order');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [orderId, onClose]);

  useEffect(() => { load(); }, [load]);

  const searchCreators = async () => {
    if (!creatorSearch.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/influencer/discover?search=${encodeURIComponent(creatorSearch)}&limit=10`);
      setCreatorResults(res.data || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const assign = async (creator) => {
    setBusy(true);
    try {
      await api.post(`/admin/package-orders/${orderId}/assign`, {
        creator_id: creator.user_id,
      });
      toast.success(`${creator.name} assigned`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally {
      setBusy(false);
    }
  };

  const removeAssignment = async (aid) => {
    setBusy(true);
    try {
      await api.delete(`/admin/package-orders/${orderId}/assign/${aid}`);
      toast.success('Removed');
      await load();
      onChange?.();
    } catch {
      toast.error('Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const updateOrder = async (patch) => {
    setBusy(true);
    try {
      await api.patch(`/admin/package-orders/${orderId}`, patch);
      toast.success('Order updated');
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-drawer">
        <DialogHeader>
          <DialogTitle>{order?.package?.name || 'Order'}</DialogTitle>
        </DialogHeader>
        {loading || !order ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-4">
            {/* Top facts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <Fact label="Client"   value={order.client_name || order.client_id} />
              <Fact label="Mode"     value={order.mode === 'auto_match' ? 'Auto-Match' : 'Browse & Pick'} />
              <Fact label="Category" value={order.category || '—'} />
              <Fact label="Amount"   value={order.amount_cents == null ? 'Quote-based' : `$${(order.amount_cents / 100).toFixed(0)}`} />
            </div>

            {/* Status changer */}
            <div>
              <Label className="text-xs">Status</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {PACKAGE_ORDER_STATUSES.map(s => {
                  const m = STATUS_META[s];
                  const active = order.status === s;
                  return (
                    <button key={s} type="button" disabled={busy}
                            onClick={() => updateOrder({ status: s })}
                            data-testid={`status-${s}`}
                            className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${active ? m.color : 'bg-white text-gray-600 border-gray-300 hover:border-cyan-400'}`}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Briefing */}
            <div className="bg-gray-50 border rounded-lg p-3 text-xs space-y-1">
              <p className="uppercase tracking-wider text-[10px] text-gray-500">Client Brief</p>
              {order.requirements?.target_audience && <p><strong>Audience:</strong> {order.requirements.target_audience}</p>}
              {order.requirements?.go_live && <p><strong>Go live:</strong> {order.requirements.go_live}</p>}
              {order.client_notes && <p className="text-gray-700"><strong>Notes:</strong> {order.client_notes}</p>}
              {!order.requirements?.target_audience && !order.requirements?.go_live && !order.client_notes && (
                <p className="text-gray-400">No brief provided.</p>
              )}
            </div>

            {/* Selected creators (browse_pick) */}
            {(order.selected_creators || []).length > 0 && (
              <div>
                <Label className="text-xs">Client-Selected Creators</Label>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {order.selected_creators.map(c => (
                    <div key={c.id} className="flex items-center gap-2 bg-white border rounded-md px-2 py-1.5 text-xs">
                      {c.profile_photo_url && <img src={c.profile_photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="text-[10px] text-gray-400">{c.influencer_tier?.toUpperCase()}</span>
                      <Button size="sm" variant="outline" disabled={busy}
                              onClick={() => assign({ user_id: c.id, name: c.name })}
                              data-testid={`assign-selected-${c.id}`}
                              className="h-6 px-2 text-[10px]">Assign</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Assigned creators */}
            <div>
              <Label className="text-xs flex items-center gap-1.5"><Users className="w-3 h-3" /> Assigned Creators</Label>
              {(order.assignments || []).length === 0 ? (
                <p className="text-[11px] text-gray-400 mt-1">No creators assigned yet.</p>
              ) : (
                <div className="mt-1 space-y-1.5">
                  {order.assignments.map(a => (
                    <div key={a.id} className="flex items-center gap-2 bg-cyan-50 border border-cyan-200 rounded-md px-2 py-1.5 text-xs" data-testid={`assignment-${a.id}`}>
                      {a.profile_photo_url && <img src={a.profile_photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
                      <span className="flex-1 truncate">{a.creator_name}</span>
                      <span className="text-[10px] text-gray-500">{a.influencer_tier?.toUpperCase() || '—'}</span>
                      <button type="button" onClick={() => removeAssignment(a.id)}
                              className="text-rose-600 hover:underline text-[10px]" data-testid={`unassign-${a.id}`}>
                        <X className="w-3 h-3 inline" /> remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add creator by search */}
              <div className="mt-2 flex gap-1.5">
                <Input placeholder="Search creator by name/city" value={creatorSearch}
                       onChange={(e) => setCreatorSearch(e.target.value)}
                       onKeyDown={(e) => e.key === 'Enter' && searchCreators()}
                       className="h-8 text-xs" data-testid="creator-search" />
                <Button size="sm" variant="outline" onClick={searchCreators} disabled={searching || !creatorSearch.trim()}
                        className="h-8 text-xs" data-testid="creator-search-btn">
                  {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                </Button>
              </div>
              {creatorResults.length > 0 && (
                <div className="mt-1.5 space-y-1.5 max-h-48 overflow-y-auto">
                  {creatorResults.map(c => (
                    <div key={c.user_id} className="flex items-center gap-2 bg-white border rounded-md px-2 py-1.5 text-xs" data-testid={`creator-result-${c.user_id}`}>
                      {c.photo && <img src={c.photo} alt="" className="w-6 h-6 rounded-full object-cover" />}
                      <span className="flex-1 truncate">{c.name} · <span className="text-gray-400">{c.city}</span></span>
                      <span className="text-[10px] text-gray-400">{c.influencer_tier?.toUpperCase()}</span>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => assign(c)}
                              data-testid={`assign-search-${c.user_id}`}
                              className="h-6 px-2 text-[10px]"><Plus className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quote (Premium custom) */}
            {order.package_slug === 'premium-custom' && (
              <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3">
                <Label className="text-xs">Custom Quote</Label>
                <div className="mt-1 flex gap-1.5">
                  <Input type="number" placeholder="$" value={quoteCents}
                         onChange={(e) => setQuoteCents(e.target.value)} className="h-8 text-xs" data-testid="quote-amount" />
                  <Button size="sm" disabled={busy}
                          onClick={() => updateOrder({ quote_amount_cents: Math.round(Number(quoteCents) * 100) })}
                          className="h-8 text-xs bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="save-quote">
                    Save Quote
                  </Button>
                </div>
                <Textarea placeholder="Quote notes (visible to client)" value={order.quote_notes || ''}
                          rows={3} className="mt-2 text-xs"
                          onBlur={(e) => updateOrder({ quote_notes: e.target.value })} data-testid="quote-notes" />
              </div>
            )}

            {/* Admin notes */}
            <div>
              <Label className="text-xs">Admin Notes (internal)</Label>
              <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)}
                        onBlur={() => adminNotes !== (order.admin_notes || '') && updateOrder({ admin_notes: adminNotes })}
                        rows={2} className="text-xs mt-1" placeholder="Internal notes…"
                        data-testid="admin-notes" />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Fact = ({ label, value }) => (
  <div className="bg-white border rounded-md p-2">
    <p className="text-[10px] uppercase tracking-wider text-gray-400">{label}</p>
    <p className="text-gray-900 font-semibold text-[12px] truncate">{value}</p>
  </div>
);

export default AdminPackages;
